import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useOffice } from '@/hooks/useOffice';

interface Tape {
  id: number;
  media_label: string;
  box_number: string | null;
  tape_type: string | null;
  media_set: string | null;
}

interface BoxGroup {
  box_number: string;
  tapes: Tape[];
}

export default function BoxesPage() {
  const { currentOfficeId } = useOffice();
  const [tapes, setTapes] = useState<Tape[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = async () => {
    if (!currentOfficeId) return;
    setLoading(true);
    setErr('');
    try {
      const { data, error } = await supabase
        .from('tapes')
        .select('id, media_label, box_number, tape_type, media_set')
        .eq('office_id', currentOfficeId)
        .not('box_number', 'is', null);
      if (error) throw error;
      setTapes(data as Tape[]);
    } catch (e: any) {
      setErr(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [currentOfficeId]);

  // Derive boxes purely from the tapes' box_number. The special
  // 'Backup Tape Library' value represents tapes sitting in the library,
  // not a physical archive box, so it is excluded here.
  const boxes = useMemo<BoxGroup[]>(() => {
    const map = new Map<string, Tape[]>();
    tapes.forEach((t) => {
      const bn = (t.box_number || '').trim();
      if (!bn || bn === 'Backup Tape Library') return;
      if (!map.has(bn)) map.set(bn, []);
      map.get(bn)!.push(t);
    });
    return Array.from(map.entries())
      .map(([box_number, list]) => ({ box_number, tapes: list }))
      .sort((a, b) => a.box_number.localeCompare(b.box_number, undefined, { numeric: true }));
  }, [tapes]);

  const filtered = useMemo(
    () =>
      boxes.filter((b) =>
        b.box_number.toLowerCase().includes(search.trim().toLowerCase())
      ),
    [boxes, search]
  );

  return (
    <div className="p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Boxes</h1>
        <p className="text-sm text-slate-500 mt-1">{boxes.length} archive boxes</p>
      </div>

      {err && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{err}</span>
          <button onClick={load} className="text-xs underline cursor-pointer">Retry</button>
        </div>
      )}

      <div className="relative mb-5 max-w-sm">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 flex items-center justify-center" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search box number..."
          className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
        />
      </div>

      {loading && <div className="text-center py-10 text-slate-400 text-sm">Loading...</div>}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-10 text-slate-400 text-sm">
          No boxes found — tapes need a Box Number assigned.
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((b) => {
            const isOpen = expanded === b.box_number;
            return (
              <div
                key={b.box_number}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden flex flex-col"
              >
                <button
                  onClick={() => setExpanded(isOpen ? null : b.box_number)}
                  className="w-full text-left px-5 py-4 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-amber-50 text-amber-700 shrink-0">
                    <i className="ri-archive-line w-5 h-5 flex items-center justify-center" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{b.box_number}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {b.tapes.length} tape{b.tapes.length === 1 ? '' : 's'} inside
                    </div>
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-700 whitespace-nowrap">
                    {b.tapes.length}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-slate-400 transition ${isOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isOpen && (
                  <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                    <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto">
                      {b.tapes.map((t) => (
                        <div
                          key={t.id}
                          className="flex items-center justify-between gap-2 px-3 py-2 rounded bg-slate-50"
                        >
                          <span className="font-mono text-xs text-slate-700 truncate">
                            {t.media_label}
                          </span>
                          {t.tape_type && (
                            <span className="text-[11px] text-slate-500 whitespace-nowrap shrink-0">
                              {t.tape_type}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}